import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LEAVE_MS, planLeave, reducedMotion } from './lib/motion';
import type { Account, AccountStatus, AiTagView, AssistantLink, TagCount, ComposeMode, Contact, Draft, Folder, MailboxRef, MessageAction, MessageSummary, ProviderPreset, Settings, UnifiedView } from '@momomail/shared';
import { DEFAULT_SETTINGS, contactFromAddress } from '@momomail/shared';
import { AddToCalendarHost } from './components/Calendar';
import { CalendarView } from './components/CalendarView';
import { Compose } from './components/Compose';
import { GoogleFlowHost, startGoogleFlow, useGoogleInfo } from './components/Google';
import { Icon } from './components/Icon';
import { MessageList, type ListFilter } from './components/MessageList';
import { ConfirmHost, Dialog, confirmDialog } from './components/Overlay';
import { Reader, ReaderEmpty } from './components/Reader';
import { Mascot } from './components/Mascot';
import { SETTINGS_TABS, SettingsDialog, ShortcutTable, type SettingsTab } from './components/Settings';
import { Sidebar, tagViewLabel } from './components/Sidebar';
import { TitleBar, type AppSection } from './components/TitleBar';
import { Toasts, toast } from './components/Toasts';
import { Welcome } from './components/Welcome';
import { api, errorText, shell } from './lib/bridge';
import { COMPOSE_FILL, assistantContext, setAssistantContext } from './lib/assistantContext';
import { screenLoading, useScreen } from './lib/screen';
import { boxOf, refOfBox } from './lib/screenRoutes';
import { ASSISTANT_OPEN, AssistantDock } from './components/Assistant';
import { WindowCat } from './components/WindowCat';
import { ScreenWork } from './components/ScreenWork';
import { todayKey } from './lib/calendar';
import { CALENDAR_GOTO } from './components/CalendarView';
import { bridge } from './lib/bridge';
import { displayName, folderName, listSep } from './lib/format';
import { applyFont, applyTheme } from './lib/theme';
import { setLanguagePref, t } from './lib/i18n';

const PAGE = 60;

function loadView(): MailboxRef | null {
  try {
    const v = JSON.parse(localStorage.getItem('momomail.view') ?? 'null') as MailboxRef | null;
    return v && (v.kind === 'folder' || v.kind === 'unified' || v.kind === 'tag') ? v : null;
  } catch {
    return null;
  }
}

function loadSection(): AppSection {
  try {
    return localStorage.getItem('momomail.section') === 'calendar' ? 'calendar' : 'mail';
  } catch {
    return 'mail';
  }
}

export function App({ banner }: { banner?: React.ReactNode }) {
  const [ready, setReady] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [statuses, setStatuses] = useState<Map<string, AccountStatus>>(new Map());
  const [folders, setFolders] = useState<Folder[]>([]);
  const [counts, setCounts] = useState<Record<UnifiedView, number>>({ inbox: 0, flagged: 0, unread: 0 });
  const [tagCounts, setTagCounts] = useState<Partial<Record<AiTagView, TagCount>>>({});
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [presets, setPresets] = useState<ProviderPreset[]>([]);

  const [view, setView] = useState<MailboxRef>(() => loadView() ?? { kind: 'unified', view: 'inbox' });
  const [filter, setFilter] = useState<ListFilter>('all');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<MessageSummary[]>([]);
  const [hasMore, setHasMore] = useState(false);
  /**
   * Scrolling to the bottom auto-loads the next page. Stop when the list is empty or the previous page turned up nothing (under the unread / starred filter, no older mail matched),
   * and switch to a button for manual loading: otherwise the loading indicator stays in view and pages through the entire mailbox one page at a time.
   */
  const [autoMore, setAutoMore] = useState(true);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [readerKey, setReaderKey] = useState(0);
  const [localDrafts, setLocalDrafts] = useState<Draft[]>([]);

  const [compose, setCompose] = useState<Draft | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<SettingsTab>('accounts');
  const [editAccount, setEditAccount] = useState<Account | 'new' | null>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [, setTick] = useState(0);
  /** Mail or calendar (title bar switch, G M / G C); remembered. */
  const [pickedSection, setSection] = useState<AppSection>(loadSection);
  /** Message rows playing their leave animation (archive / delete / move); the ref guards against acting on them twice. */
  const [leavingIds, setLeavingIds] = useState<ReadonlySet<number>>(() => new Set());
  const leavingRef = useRef<ReadonlySet<number>>(new Set());
  /** Which section the user just switched to, so only a real switch slides in (not the first render). */
  const [switchedTo, setSwitchedTo] = useState<AppSection | null>(null);
  const switchSection = useCallback((next: AppSection) => {
    setSection(next);
    setSwitchedTo(next);
  }, []);
  /** Whether any calendar account is connected: the mail / calendar switch only exists then (until then it's just mail). */
  const [hasCalendar, setHasCalendar] = useState(false);
  const loadCalendarAccounts = useCallback(() => {
    void api()
      .calendar.info()
      .then((info) => setHasCalendar((info.accounts?.length ?? 0) > 0))
      .catch(() => {});
  }, []);
  useEffect(loadCalendarAccounts, [loadCalendarAccounts]);
  const section: AppSection = hasCalendar ? pickedSection : 'mail';
  const lastG = useRef(0);
  useEffect(() => {
    try {
      localStorage.setItem('momomail.section', pickedSection);
    } catch {
      /* ignore */
    }
  }, [pickedSection]);

  const searchRef = useRef<HTMLInputElement>(null);
  const listReq = useRef(0);
  const metaReq = useRef(0);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const selectedRef = useRef(selectedId);
  selectedRef.current = selectedId;
  /**
   * A message opened by id (from 馍馍, a notification): it may be far down its folder (past the first page) or not the newest of its
   * conversation, so not among the rows loaded — it goes into the list where its date puts it, and stays selected.
   */
  const pinnedRef = useRef<MessageSummary | null>(null);

  const accountMap = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);
  const folderMap = useMemo(() => new Map(folders.map((f) => [f.id, f])), [folders]);

  // Use the search ref while searching, otherwise the current folder / unified view
  const scopeAccount = view.kind === 'folder' ? (folderMap.get(view.folderId)?.accountId ?? null) : null;
  const ref: MailboxRef = query ? { kind: 'search', query, accountId: scopeAccount } : view;
  const conversation = settings.conversationView && ref.kind !== 'search';

  // ---------------- Data ----------------

  const loadMeta = useCallback(async () => {
    const b = api();
    const id = ++metaReq.current;
    const [acc, st, fl, ct] = await Promise.all([b.accounts.list(), b.accounts.status(), b.mail.folders(null), b.mail.unifiedCounts()]);
    // A later request has started meanwhile: its newer numbers win
    if (id !== metaReq.current) return { acc, fl };
    // (AI tags: from the backend only, so not when it can't be reached; older backends don't have them)
    void b.mail
      .tagCounts()
      .then((tc) => id === metaReq.current && setTagCounts(tc))
      .catch(() => {});
    setAccounts(acc);
    setStatuses(new Map(st.map((s) => [s.accountId, s])));
    setFolders(fl);
    setCounts(ct);
    return { acc, fl };
  }, []);

  const loadList = useCallback(
    async (mode: 'reset' | 'refresh' | 'more') => {
      const id = ++listReq.current;
      if (mode === 'reset') {
        setListLoading(true);
        setMessages([]);
        setHasMore(false);
      }
      if (mode === 'more') setListLoading(true);
      try {
        const current = messagesRef.current;
        const last = current.at(-1);
        const result = await api().mail.list({
          ref,
          filter,
          // Clicking "更早的" (older) with an empty list: page back from the very end (the backend treats before = null as the first page)
          before: mode === 'more' ? (last ? { date: last.date, id: last.id } : { date: Number.MAX_SAFE_INTEGER, id: Number.MAX_SAFE_INTEGER }) : null,
          limit: mode === 'refresh' ? Math.max(PAGE, current.length) : PAGE,
          accountId: null,
        });
        if (id !== listReq.current) return;
        let next = mode === 'more' ? [...current, ...result.messages] : result.messages;
        // Under the "未读 / 星标" (unread / starred) filter, the message being read becomes read as soon as it opens and no longer matches; keep it in place on refresh,
        // otherwise it vanishes from the list and the right pane goes blank too. Only actually remove it when switching folder or filter (reset).
        const pinned = pinnedRef.current;
        const pinnedHere = pinned && pinned.id === selectedRef.current && ref.kind === 'folder' && ref.folderId === pinned.folderId && filter === 'all' ? pinned : undefined;
        const keep =
          (mode === 'refresh' && filter !== 'all' ? current.find((m) => m.id === selectedRef.current) : undefined) ??
          // (in conversation view its thread's row stands for it)
          (pinnedHere && !next.some((m) => m.id === pinnedHere.id || (conversation && m.threadId === pinnedHere.threadId)) ? pinnedHere : undefined);
        if (keep && !next.some((m) => m.id === keep.id)) {
          const at = next.findIndex((m) => m.date < keep.date || (m.date === keep.date && m.id < keep.id));
          next = at < 0 ? [...next, keep] : [...next.slice(0, at), keep, ...next.slice(at)];
        }
        setMessages(next);
        setHasMore(result.hasMore);
        if (mode === 'more') setAutoMore(result.messages.length > 0);
        else if (mode === 'reset') setAutoMore(next.length > 0);
        setListError(null);
      } catch (e) {
        if (id === listReq.current) setListError(errorText(e));
      } finally {
        if (id === listReq.current) setListLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(ref), filter, conversation],
  );

  useEffect(() => {
    const b = api();
    void Promise.all([loadMeta(), b.settings.get(), b.accounts.presets(), b.compose.localDrafts()]).then(([meta, s, p, drafts]) => {
      setSettings(s);
      // The tagging AI reads the times in mail in the user's time zone: tell the server this computer's when it has another
      const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (zone && zone !== s.timeZone) void b.settings.update({ timeZone: zone }).then(setSettings, () => {});
      applyTheme(s.theme);
      applyFont(s.font);
      setLanguagePref(s.language);
      setPresets(p);
      setLocalDrafts(drafts);
      // The last-viewed folder is gone: go back to All Inboxes / the only account's inbox
      const v = loadView();
      const valid = v && (v.kind === 'unified' || v.kind === 'tag' ? meta.acc.length > 0 : v.kind === 'folder' && meta.fl.some((f) => f.id === v.folderId));
      if (!valid) {
        const inbox = meta.acc.length === 1 ? meta.fl.find((f) => f.accountId === meta.acc[0]!.id && f.role === 'inbox') : undefined;
        setView(inbox ? { kind: 'folder', folderId: inbox.id } : { kind: 'unified', view: 'inbox' });
      }
      setReady(true);
    });
    const timer = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(timer);
  }, [loadMeta]);

  useEffect(() => {
    if (!ready) return;
    void loadList('reset');
  }, [ready, loadList]);

  useEffect(() => {
    if (view.kind === 'folder' || view.kind === 'unified' || view.kind === 'tag') {
      try {
        localStorage.setItem('momomail.view', JSON.stringify(view));
      } catch {
        /* ignore */
      }
    }
  }, [view]);

  // Debounce search box input
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // First account added: jump from the welcome page to its inbox
  useEffect(() => {
    if (accounts.length === 1 && view.kind === 'unified' && view.view === 'inbox') {
      const inbox = folders.find((f) => f.accountId === accounts[0]!.id && f.role === 'inbox');
      if (inbox) setView({ kind: 'folder', folderId: inbox.id });
    }
  }, [accounts, folders, view]);

  // ---------------- Events pushed from the main process ----------------

  const openMessageRef = useRef<(id: number) => void>(() => {});
  const followLinkRef = useRef<(link: AssistantLink) => void>(() => {});
  const startComposeRef = useRef<(mode: ComposeMode, source: number | null, to?: string | null, fill?: { subject: string; body: string }) => void>(() => {});

  useEffect(
    () =>
      api().onEvent((event) => {
        switch (event.type) {
          case 'changed':
            void loadMeta();
            void loadList('refresh');
            void api().compose.localDrafts().then(setLocalDrafts);
            setReaderKey((k) => k + 1);
            break;
          case 'status':
            setStatuses((m) => new Map(m).set(event.status.accountId, event.status));
            break;
          case 'calendar':
            loadCalendarAccounts();
            break;
          case 'settings':
            setSettings(event.settings);
            applyTheme(event.settings.theme);
            applyFont(event.settings.font);
            setLanguagePref(event.settings.language);
            break;
          case 'open-message':
            openMessageRef.current(event.messageId);
            break;
          case 'compose':
            startComposeRef.current('new', null, event.to);
            break;
          case 'open-link':
            followLinkRef.current(event.link);
            break;
          case 'navigate':
            if (event.target === 'settings' || event.target === 'connection') {
              setSettingsTab(event.target === 'connection' ? 'connection' : 'accounts');
              setSettingsOpen(true);
            } else if (event.target === 'calendar-today') {
              // From the desktop cat's menu
              switchSection('calendar'); // (stable: a useCallback with no deps)
              setTimeout(() => window.dispatchEvent(new CustomEvent(CALENDAR_GOTO, { detail: todayKey() })), 0);
            } else if (event.target === 'assistant') window.dispatchEvent(new Event(ASSISTANT_OPEN));
            else setShortcutsOpen(true);
            break;
          case 'toast':
            toast(event.text, event.kind);
            break;
        }
      }),
    [loadMeta, loadList],
  );

  // ---------------- Actions ----------------

  const selected =
    messages.find((m) => m.id === selectedId) ??
    // (opened by id in conversation view: its conversation's row)
    (pinnedRef.current?.id === selectedId ? messages.find((m) => conversation && m.threadId === pinnedRef.current!.threadId) : undefined) ??
    null;

  // For 馍馍: where the user is and what they read (it goes along with their chat messages)
  useEffect(() => {
    const folder = view.kind === 'folder' ? folderMap.get(view.folderId) : undefined;
    const mailboxName = query
      ? t('搜索结果：{query}', { query })
      : folder
        ? folderName(folder)
        : view.kind === 'tag'
          ? tagViewLabel(view.tag)
          : view.kind === 'unified' && view.view === 'flagged'
            ? t('星标邮件')
            : t('全部收件箱');
    setAssistantContext({
      section,
      mailbox: ref,
      mailboxName,
      message: section === 'mail' && selected ? { id: selected.id, subject: selected.subject, from: displayName(selected.from) } : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, JSON.stringify(ref), query, folderMap, selected?.id]);

  const markSeen = useCallback(
    (m: MessageSummary) => {
      if (m.seen && m.threadUnread === 0) return;
      setMessages((list) => list.map((x) => (x.id === m.id ? { ...x, seen: true, threadUnread: 0 } : x)));
      // The sidebar's numbers go down at once (counted here: the unread ones just read, in this message's folder), then the real ones
      // come from the backend once it has marked them
      const n = Math.max(conversation ? m.threadUnread : 0, m.seen ? 0 : 1);
      const inInbox = folderMap.get(m.folderId)?.role === 'inbox';
      setFolders((list) => list.map((f) => (f.id === m.folderId ? { ...f, unread: Math.max(0, f.unread - n) } : f)));
      if (inInbox) setCounts((c) => ({ ...c, inbox: Math.max(0, c.inbox - n), unread: Math.max(0, c.unread - n) }));
      void api()
        .mail.act([m.id], { kind: 'seen', value: true }, { conversation, ref })
        .then(() => loadMeta())
        .catch((e) => toast(errorText(e), 'error'));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversation, JSON.stringify(ref), folderMap, loadMeta],
  );

  const select = useCallback(
    (m: MessageSummary) => {
      pinnedRef.current = null;
      setSelectedId(m.id);
      markSeen(m);
      // Prefetch the next few messages in the list while we're at it (so J / K paging doesn't wait)
      const list = messagesRef.current;
      const i = list.findIndex((x) => x.id === m.id);
      if (i >= 0) api().mail.prefetch([list[i + 1], list[i + 2], list[i - 1]].filter((x): x is MessageSummary => !!x).map((x) => x.id), conversation);
    },
    [markSeen, conversation],
  );

  openMessageRef.current = (id: number) => {
    // Opened from a notification: switch to that message's folder and select it
    void screenLoading(api().mail.thread(id, false, false))
      .then((thread) => {
        const m = thread.messages.find((x) => x.id === id) ?? thread.messages[0];
        if (!m) return;
        setSearch('');
        setQuery('');
        setFilter('all');
        pinnedRef.current = m;
        setSelectedId(m.id);
        // Already showing that folder: no reload comes, put it in now
        if (view.kind === 'folder' && view.folderId === m.folderId) void loadList('refresh');
        else setView({ kind: 'folder', folderId: m.folderId });
        if (!m.seen) void api().mail.act([m.id], { kind: 'seen', value: true }, null);
      })
      .catch(() => toast(t('那封邮件已经不在了'), 'error'));
  };

  const act = useCallback(
    async (action: MessageAction, requested: number[]) => {
      let ids = requested;
      if (!ids.length) return;
      const removes = action.kind === 'archive' || action.kind === 'trash' || action.kind === 'spam' || action.kind === 'move' || action.kind === 'deleteForever';
      if (removes) {
        // Rows already on their way out are skipped, so repeated clicks / keys can't act twice
        const plan = planLeave(messages, ids, leavingRef.current, selectedId);
        if (!plan.ids.length) return;
        ids = plan.ids;
        // Select the next one right away, then let the rows slide out and close up before the list drops them (like Gmail)
        if (plan.next !== undefined) {
          setSelectedId(plan.next ? plan.next.id : null);
          if (plan.next) markSeen(plan.next);
        }
        const gone = ids;
        setLeavingIds((s) => new Set([...s, ...gone]));
        leavingRef.current = new Set([...leavingRef.current, ...gone]);
        if (!reducedMotion()) await new Promise((r) => setTimeout(r, LEAVE_MS));
        setMessages((list) => list.filter((m) => !gone.includes(m.id)));
        const rest = new Set(leavingRef.current);
        for (const id of gone) rest.delete(id);
        leavingRef.current = rest;
        setLeavingIds(rest);
      } else if (action.kind === 'seen' || action.kind === 'flagged') {
        setMessages((list) =>
          list.map((m) =>
            ids.includes(m.id) ? (action.kind === 'seen' ? { ...m, seen: action.value, threadUnread: action.value ? 0 : Math.max(1, m.threadUnread) } : { ...m, flagged: action.value }) : m,
          ),
        );
      }
      try {
        await api().mail.act(ids, action, { conversation, ref });
        const label: Partial<Record<MessageAction['kind'], string>> = { archive: '已归档', trash: '已删除', spam: '已移到垃圾邮件', move: '已移动', deleteForever: '已彻底删除' }; // i18n-ok
        if (label[action.kind]) toast(t(label[action.kind]!), 'ok');
      } catch (e) {
        toast(errorText(e), 'error');
        void loadList('refresh');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages, selectedId, conversation, JSON.stringify(ref), markSeen, loadList],
  );

  const startCompose = useCallback(
    async (mode: ComposeMode, source: number | null, to?: string | null, fill?: { subject: string; body: string }) => {
      if (!accounts.length) {
        toast(t('先添加一个邮箱账户'), 'info');
        setSettingsTab('accounts');
        setEditAccount('new');
        setSettingsOpen(true);
        return;
      }
      if (compose) {
        toast(t('先把正在写的这封发出去或者存成草稿'), 'info');
        return;
      }
      try {
        const accountId = view.kind === 'folder' ? (folderMap.get(view.folderId)?.accountId ?? null) : null;
        const draft = await screenLoading(api().compose.create(mode, source, accountId));
        if (to) draft.to = to.split(/[,;]/).map((a) => ({ name: '', address: a.trim() })).filter((a) => a.address);
        if (fill?.subject) draft.subject = fill.subject;
        if (fill?.body) draft.body = fill.body;
        setCompose(draft);
      } catch (e) {
        toast(errorText(e), 'error');
      }
    },
    [accounts.length, compose, view, folderMap],
  );
  startComposeRef.current = (mode, source, to, fill) => void startCompose(mode, source, to, fill);

  const syncNow = useCallback(() => {
    void api()
      .mail.sync(null)
      .catch((e) => toast(errorText(e), 'error'));
  }, []);

  const markAllRead = useCallback(() => {
    setMessages((list) => list.map((m) => ({ ...m, seen: true, threadUnread: 0 })));
    void api()
      .mail.markAllRead(ref, null)
      .then(() => toast(t('都标成已读了'), 'ok'))
      .catch((e) => toast(errorText(e), 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(ref)]);

  /** The junk tag's button: everything the AI called junk goes to each account's spam folder (after asking). */
  const junkToSpam = async () => {
    const n = tagCounts.junk?.total ?? messages.length;
    const answer = await confirmDialog({
      title: t('全部移到垃圾邮件？'),
      message: t('AI 标为"垃圾"的 {n} 封邮件会移到各自邮箱的垃圾邮件文件夹（Gmail 等网页上也一样）。移错了可以从垃圾邮件里移回来。', { n }),
      confirm: t('移到垃圾邮件'),
    });
    if (answer !== 'confirm') return;
    try {
      const moved = await api().mail.junkToSpam();
      toast(t('移走了 {n} 封', { n: moved }), 'ok');
    } catch (e) {
      toast(errorText(e), 'error');
    }
    void loadMeta();
    void loadList('reset');
  };

  /** Mark the whole folder on the server (unified view: every account's inbox) as read, including older mail not downloaded yet. */
  const markAllReadOnServer = useCallback(async () => {
    if (ref.kind === 'search') return;
    const folder = ref.kind === 'folder' ? folderMap.get(ref.folderId) : undefined;
    const where = folder
      ? t('{email} 的"{folder}"', { email: accountMap.get(folder.accountId)?.email ?? '', folder: folderName(folder) })
      : accounts.length === 1
        ? t('1 个账户的收件箱')
        : t('{n} 个账户的收件箱', { n: accounts.length });
    const answer = await confirmDialog({
      title: t('服务器上全部标为已读？'),
      message: t('{where}里的所有邮件都会标为已读，包括还没下载到馍馍邮箱的老邮件。Gmail 等邮箱里也会同时变成已读，没法一键撤销。', { where }),
      confirm: t('全部标为已读'),
    });
    if (answer !== 'confirm') return;
    setMessages((list) => list.map((m) => ({ ...m, seen: true, threadUnread: 0 })));
    toast(t('正在服务器上标为已读…'), 'info');
    try {
      await api().mail.markAllRead(ref, null, true);
      toast(t('服务器上的邮件都标成已读了'), 'ok');
    } catch (e) {
      toast(errorText(e), 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(ref), folderMap, accountMap, accounts.length]);

  const changeView = (next: MailboxRef) => {
    setView(next);
    setSearch('');
    setQuery('');
    setSelectedId(null);
  };

  // ---------------- Keyboard shortcuts ----------------

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      const typing = target?.closest('input, textarea, select, [contenteditable="true"]');
      if (e.key === 'F5') {
        e.preventDefault();
        syncNow();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === ',') {
        e.preventDefault();
        setSettingsOpen(true);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'n' || e.key === 'N')) {
        e.preventDefault();
        void startCompose('new', null);
        return;
      }
      if (typing || e.ctrlKey || e.metaKey || e.altKey) return;
      if (compose || settingsOpen || shortcutsOpen || document.querySelector('.dialog')) return;
      // G then M / C: switch between mail and calendar
      if (e.key === 'g') {
        lastG.current = Date.now();
        return;
      }
      if (hasCalendar && Date.now() - lastG.current < 1200 && (e.key === 'm' || e.key === 'c')) {
        lastG.current = 0;
        e.preventDefault();
        switchSection(e.key === 'c' ? 'calendar' : 'mail');
        return;
      }
      // The calendar has its own keys (CalendarView)
      if (section === 'calendar') return;
      const idx = messages.findIndex((m) => m.id === selectedId);
      const move = (d: number) => {
        const next = messages[Math.max(0, Math.min(messages.length - 1, (idx < 0 ? (d > 0 ? -1 : messages.length) : idx) + d))];
        if (next) select(next);
      };
      const key = e.key;
      const sel = selectedId;
      const handlers: Record<string, () => void> = {
        j: () => move(1),
        ArrowDown: () => move(1),
        k: () => move(-1),
        ArrowUp: () => move(-1),
        c: () => void startCompose('new', null),
        '/': () => searchRef.current?.focus(),
        '?': () => setShortcutsOpen(true),
        I: () => e.shiftKey && markAllRead(),
        Escape: () => (search ? setSearch('') : setSelectedId(null)),
      };
      if (sel != null && selected) {
        Object.assign(handlers, {
          r: () => void startCompose('reply', sel),
          a: () => void startCompose('replyAll', sel),
          f: () => void startCompose('forward', sel),
          e: () => void act({ kind: 'archive' }, [sel]),
          Delete: () => void act({ kind: 'trash' }, [sel]),
          '#': () => void act({ kind: 'trash' }, [sel]),
          s: () => void act({ kind: 'flagged', value: !selected.flagged }, [sel]),
          u: () => void act({ kind: 'seen', value: !(selected.seen && selected.threadUnread === 0) }, [sel]),
          v: () => document.querySelector<HTMLButtonElement>('.reader__move .tool')?.click(),
        });
      }
      const h = handlers[key];
      if (h) {
        e.preventDefault();
        h();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [messages, selectedId, selected, compose, settingsOpen, shortcutsOpen, search, act, startCompose, syncNow, select, markAllRead, section, hasCalendar]);

  // ---------------- View ----------------

  // Recipient suggestions normally come from the backend (all correspondence); if the backend is unreachable, fall back to addresses in the current list
  const contacts = useMemo(() => {
    const seen = new Map<string, Contact>();
    for (const m of messages) for (const a of [m.from, ...m.to]) if (a.address && !seen.has(a.address.toLowerCase())) seen.set(a.address.toLowerCase(), contactFromAddress(a, m.date));
    for (const a of accounts) seen.delete(a.email.toLowerCase());
    return [...seen.values()];
  }, [messages, accounts]);

  const syncing = [...statuses.values()].some((s) => s.state === 'syncing' || s.state === 'connecting');

  // 馍馍 working this window (lib/screen.ts): where the user is as a route, and going to one with the app's own ways there
  useScreen({
    current: () => {
      if (settingsOpen) return { page: 'settings', section: settingsTab };
      if (compose) return { page: 'compose', mode: compose.mode, id: compose.sourceMessageId };
      if (section === 'calendar') return { page: 'calendar' };
      if (selectedId != null) return { page: 'message', id: selectedId };
      return { page: 'mail', box: boxOf(ref) };
    },
    navigate: async (place) => {
      if (place.page === 'settings') {
        if (!SETTINGS_TABS.includes(place.section as SettingsTab)) throw new Error(`No settings page ${place.section} (they are: ${SETTINGS_TABS.join(', ')})`);
        setSettingsTab(place.section as SettingsTab);
        setSettingsOpen(true);
        return;
      }
      if (place.page === 'compose' && compose) throw new Error('An email is being written already: it has to be sent or saved as a draft (by the user) before another');
      if (place.page === 'calendar' && !hasCalendar) throw new Error('No calendar account is connected');
      setSettingsOpen(false);
      setEditAccount(null);
      if (place.page === 'calendar') return switchSection('calendar');
      if (place.page === 'compose') return startCompose(place.mode, place.id);
      switchSection('mail');
      if (place.page === 'message') return openMessageRef.current(place.id);
      const next = refOfBox(place.box, folders);
      if (next.kind === 'search') {
        changeView(view);
        setSearch(next.query);
        setQuery(next.query);
      } else changeView(next);
    },
    // (a search typed in waits out its debounce before it loads)
    loading: () => listLoading || search.trim() !== query,
  });

  if (!ready) {
    return (
      <div className="app app--loading">
        <span className="spinner spinner--big" />
      </div>
    );
  }

  // 馍馍's buttons (in its chat here, or in the desktop cat's): open a message (in mail), a day (in the calendar), a settings page or
  // a draft it wrote (in compose)
  const followLink = (link: AssistantLink) => {
    if (link.kind === 'message') {
      switchSection('mail');
      openMessageRef.current(link.messageId);
    } else if (link.kind === 'calendar') {
      switchSection('calendar');
      // The calendar may only mount now: tell it after this render
      setTimeout(() => window.dispatchEvent(new CustomEvent(CALENDAR_GOTO, { detail: link.date })), 0);
    } else if (link.kind === 'settings') {
      setSettingsTab(SETTINGS_TABS.includes(link.tab as SettingsTab) ? (link.tab as SettingsTab) : 'general');
      setSettingsOpen(true);
    } else if (link.kind === 'draft') {
      switchSection('mail');
      startComposeRef.current('draft', link.messageId);
    } else if (link.kind === 'fill-compose') {
      // Into the email being written; none open (any more): a new one with it
      if (assistantContext().compose) window.dispatchEvent(new CustomEvent(COMPOSE_FILL, { detail: link }));
      else startComposeRef.current('new', null, link.to || null, { subject: link.subject ?? '', body: link.body ?? '' });
    } else if (link.kind === 'mailbox') {
      switchSection('mail');
      if (link.ref.kind === 'search') {
        changeView(view);
        setSearch(link.ref.query);
        setQuery(link.ref.query);
      } else changeView(link.ref);
    } else {
      switchSection('mail');
      // A reply / forward: the message first, then its reply with the text in it (as if the user pressed Reply)
      if (link.mode && link.mode !== 'new' && link.replyTo) {
        openMessageRef.current(link.replyTo);
        startComposeRef.current(link.mode, link.replyTo, link.to || null, { subject: link.subject, body: link.body });
      } else startComposeRef.current('new', null, link.to || null, { subject: link.subject, body: link.body });
    }
  };
  followLinkRef.current = followLink;
  // The cat is at home in its bed on the web, and in the desktop app while the desktop cat is off (sent home, or dropped into the bed);
  // out, it comes into this window when it opens (WindowCat). On the desktop it can be picked up out of the bed and dropped back in
  const assistant = (
    <>
      <AssistantDock onFollow={followLink} catHome={!shell || !settings.desktopMascot} canPickUp={!!shell?.cat} />
      <WindowCat enabled={!!shell} out={settings.desktopMascot} />
      <ScreenWork />
    </>
  );

  const settingsDialog = (
    <SettingsDialog
      open={settingsOpen}
      tab={settingsTab}
      onTab={setSettingsTab}
      onClose={() => {
        setSettingsOpen(false);
        setEditAccount(null);
      }}
      accounts={accounts}
      statuses={statuses}
      presets={presets}
      settings={settings}
      onSettings={(s) => {
        setSettings(s);
        applyTheme(s.theme);
        applyFont(s.font);
        setLanguagePref(s.language);
      }}
      editAccount={editAccount}
      onEditAccount={setEditAccount}
    />
  );

  const titlebar = (
    <TitleBar
      syncing={syncing}
      onSync={syncNow}
      onSettings={() => setSettingsOpen(true)}
      onCompose={() => void startCompose('new', null)}
      onShortcuts={() => setShortcutsOpen(true)}
      {...(hasCalendar ? { section, onSection: switchSection } : {})}
    />
  );

  const openCalendarSettings = () => {
    setSettingsTab('calendar');
    setEditAccount(null);
    setSettingsOpen(true);
  };

  // Calendar accounts don't need a mail account: the calendar works on its own
  if (section === 'calendar') {
    return (
      <div className="app">
        {titlebar}
        {banner}
        <div className={`section ${switchedTo === 'calendar' ? 'section--from-right' : ''}`}>
          <CalendarView onOpenSettings={openCalendarSettings} />
        </div>
        {compose && (
          <Compose
            key={compose.id}
            initial={compose}
            accounts={accounts}
            contacts={contacts}
            onDone={() => {
              setCompose(null);
              void api().compose.localDrafts().then(setLocalDrafts);
            }}
          />
        )}
        {settingsDialog}
        {assistant}
        <Dialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} title={t('键盘快捷键')} labelledBy="shortcuts-title" className="dialog__card--small">
          <ShortcutTable />
        </Dialog>
        <ConfirmHost />
        <GoogleFlowHost />
        <Toasts />
      </div>
    );
  }

  if (!accounts.length) {
    return (
      <div className="app">
        {titlebar}
        {banner}
        <Welcome presets={presets} mode={bridge.connection.info().mode} />
        {settingsDialog}
        <ConfirmHost />
        <GoogleFlowHost />
        <Toasts />
      </div>
    );
  }

  const currentFolder = view.kind === 'folder' ? folderMap.get(view.folderId) : undefined;
  const tagView = view.kind === 'tag' ? view.tag : null;
  const title = query ? t('搜索结果') : currentFolder ? folderName(currentFolder) : tagView ? tagViewLabel(tagView) : view.kind === 'unified' && view.view === 'flagged' ? t('星标邮件') : t('全部收件箱');
  const subtitleAccount = currentFolder ? accountMap.get(currentFolder.accountId) : undefined;
  const nAccounts = accounts.length === 1 ? t('1 个账户') : t('{n} 个账户', { n: accounts.length });
  const nMessages = (n: number) => (n === 1 ? t('1 封') : t('{n} 封', { n }));
  const subtitle = query
    ? `"${query}" · ${subtitleAccount ? subtitleAccount.name : t('所有账户')}`
    : subtitleAccount
      ? `${subtitleAccount.name} · ${currentFolder!.unread ? t('{n} 封未读', { n: currentFolder!.unread }) : nMessages(currentFolder!.total)}`
      : tagView
        ? `${t('AI 标签')} · ${nMessages(tagCounts[tagView]?.total ?? 0)}`
        : view.kind === 'unified' && view.view === 'flagged'
          ? `${nAccounts} · ${nMessages(counts.flagged)}`
          : `${nAccounts} · ${counts.inbox ? t('{n} 封未读', { n: counts.inbox }) : t('没有未读')}`;
  const role = currentFolder?.role ?? null;
  const unsynced = localDrafts.filter((d) => d.serverMessageId == null && (!currentFolder || d.accountId === currentFolder.accountId));
  const emptyHint = query ? (
    <EmptyList title={t('没找到')} text={t('没有邮件包含"{query}"。试试换个词，或者少输几个字。', { query })} />
  ) : filter !== 'all' ? (
    <EmptyList title={filter === 'unread' ? t('没有未读邮件') : t('没有星标邮件')} text={t('换回「全部」看看。')} />
  ) : (
    tagView ? (
    <EmptyList title={t('这里空空的')} text={t('AI 给新邮件打上这个标签后，会出现在这里。')} />
  ) : (
    <EmptyList title={role === 'inbox' || view.kind === 'unified' ? t('收件箱是空的') : t('这里空空的')} text={role === 'trash' ? t('删掉的邮件会放在这里。') : role === 'drafts' ? t('存的草稿会出现在这里。') : t('有新邮件时会马上出现。')} />
  )
  );

  return (
    <div className="app">
      {titlebar}
      {banner}
      <div className={`layout ${switchedTo === 'mail' ? 'section--from-left' : ''}`}>
        <Sidebar
          accounts={accounts}
          folders={folders}
          statuses={statuses}
          counts={counts}
          tagCounts={tagCounts}
          tagLabels={settings.aiLabels.map((l) => l.name)}
          current={view}
          onSelect={changeView}
          onCompose={() => void startCompose('new', null)}
          onAddAccount={() => {
            setSettingsTab('accounts');
            setEditAccount('new');
            setSettingsOpen(true);
          }}
          onRetry={(id) => void api().mail.sync(id)}
          onDropMessages={(folderId, ids) => void act({ kind: 'move', folderId }, ids)}
        />
        <MessageList
          leaving={leavingIds}
          title={title}
          subtitle={subtitle}
          messages={messages}
          hasMore={hasMore}
          autoMore={autoMore}
          loading={listLoading}
          error={listError}
          selectedId={selectedId}
          filter={filter}
          search={search}
          accounts={accountMap}
          showAccount={!currentFolder && accounts.length > 1}
          sentView={role === 'sent' || role === 'drafts'}
          emptyHint={emptyHint}
          topSlot={
            <>
              <AuthBanner
                accounts={accounts}
                statuses={statuses}
                onFixPassword={(a) => {
                  setSettingsTab('accounts');
                  setEditAccount(a);
                  setSettingsOpen(true);
                }}
              />
              {tagView === 'junk' && messages.length > 0 && (
                <div className="tag-junk">
                  <span>{t('AI 认为这些是垃圾邮件。')}</span>
                  <button className="btn btn--small" onClick={() => void junkToSpam()}>
                    <Icon name="spam" size={14} /> {t('全部移到垃圾邮件')}
                  </button>
                </div>
              )}
              {role === 'drafts' && unsynced.length > 0 ? (
              <div className="local-drafts">
                <div className="local-drafts__title">
                  <Icon name="offline" size={14} /> {t('只存在本机、还没上传的草稿')}
                </div>
                {unsynced.map((d) => (
                  <button key={d.id} className="local-draft" onClick={() => (compose ? toast(t('先把正在写的这封处理完'), 'info') : setCompose(d))}>
                    <strong>{d.subject || t('(无主题)')}</strong>
                    <span className="muted">{d.to.map((a) => a.name || a.address).join(listSep()) || t('(无收件人)')}</span>
                  </button>
                ))}
              </div>
            ) : null}
            </>
          }
          onFilter={setFilter}
          onSearch={setSearch}
          onSelect={select}
          onOpen={(m) => {
            if (role === 'drafts') void startCompose('draft', m.id);
          }}
          onToggleFlag={(m) => void act({ kind: 'flagged', value: !m.flagged }, [m.id])}
          onLoadMore={() => void loadList('more')}
          onMarkAllRead={markAllRead}
          onMarkAllReadOnServer={query || tagView ? null : () => void markAllReadOnServer()}
          unreadTotal={query ? null : currentFolder ? currentFolder.unread : view.kind === 'unified' && view.view !== 'flagged' ? counts.inbox : null}
          onRetry={() => void loadList('reset')}
          searchRef={searchRef}
        />
        {selected ? (
          <Reader
            messageId={selected.id}
            conversation={conversation}
            account={accountMap.get(selected.accountId)}
            folders={folders}
            refreshKey={readerKey}
            onReply={(mode, id) => void startCompose(mode, id)}
            onAction={(action, ids) => void act(action, ids)}
            onEditDraft={(id) => void startCompose('draft', id)}
          />
        ) : (
          <ReaderEmpty unread={counts.inbox} />
        )}
      </div>
      {compose && (
        <Compose
          key={compose.id}
          initial={compose}
          accounts={accounts}
          contacts={contacts}
          onDone={() => {
            setCompose(null);
            void api().compose.localDrafts().then(setLocalDrafts);
          }}
        />
      )}
      {settingsDialog}
      {assistant}
      <Dialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} title={t('键盘快捷键')} labelledBy="shortcuts-title" className="dialog__card--small">
        <ShortcutTable />
      </Dialog>
      <ConfirmHost />
      <GoogleFlowHost />
      <AddToCalendarHost />
      <Toasts />
    </div>
  );
}

function EmptyList({ title, text }: { title: string; text: string }) {
  return (
    <div className="empty empty--list">
      <div className="empty__art empty__art--small" aria-hidden>
        <Mascot />
      </div>
      <h2>{title}</h2>
      <p className="muted">{text}</p>
    </div>
  );
}

/** A mailbox's sign-in has expired (password changed, Google authorization revoked): show a notice above the list with a one-click fix. */
function AuthBanner({ accounts, statuses, onFixPassword }: { accounts: Account[]; statuses: Map<string, AccountStatus>; onFixPassword: (a: Account) => void }) {
  const google = useGoogleInfo();
  const broken = accounts.filter((a) => statuses.get(a.id)?.authError);
  if (!broken.length) return null;
  return (
    <div className="auth-banners">
      {broken.map((a) => (
        <div key={a.id} className="notice notice--error auth-banner" role="alert">
          <Icon name="alert" size={15} />
          <span>
            <b>{a.email}</b> {t('登录失效，收不到新邮件')}
          </span>
          {a.authKind === 'oauth2' ? (
            google?.google ? (
              <button className="btn btn--small" onClick={() => void startGoogleFlow({ purpose: 'mail', accountId: a.id, loginHint: a.email })}>
                {t('重新用 Google 登录')}
              </button>
            ) : (
              <span className="muted">{t('请管理员在服务器上运行 account reauth')}</span>
            )
          ) : (
            <button className="btn btn--small" onClick={() => onFixPassword(a)}>
              {t('重新填写密码')}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
